import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../types/common/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { PackageViewData } from '../types/PackageViewData';
import {
  WEB_COMPONENT_DOWNLOAD_TYPE_FORM,
  WEB_COMPONENT_PACKAGE_DEPLOYER_FORM,
  WEB_COMPONENT_PACKAGE_GENERAL_FORM,
  WEB_COMPONENT_PACKAGE_MISCELLANEOUS_FORM,
  WEB_COMPONENT_PACKAGE_MODULES_FORM,
  WEB_COMPONENT_PACKAGE_SCAN_MODES_FORM,
  WEB_COMPONENT_PACKAGE_SECURITY_SERVER_ASSIGNMENT_GRID,
  WEB_COMPONENT_PACKAGE_SETTINGS_FORM,
  WEB_COMPONENT_PACKAGE_USE_PROXY_FORM,
} from '../../../webcomponents.constants';
import { PackageGeneralFormWebComponent } from '../../common/packageGeneralForm';
import { PackageModulesFormWebComponent } from '../../common/packageModulesForm';
import { PackageEvent } from '../types/PackageEvent';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { PackageScanModesFormWebComponent } from '../../common/packageScanModesForm';
import { PackageDeployerFormWebComponent } from '../../common/packageDeployerForm';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { PackageData } from '../../types/package-data';
import { PackageMiscellaneousFormWebComponent } from '../../common/packageMiscellaneousForm';
import { PackageSecurityServerAssignmentGridWebComponent } from '../../common/packageSecurityServerAssignmentGrid';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';
import { htmlEncode } from '../../../utils/escape.utils';
import { PackageSettingsFormWebComponent } from '../../common/packageSettingsForm';
import { PackageUseProxyFormWebComponent } from '../../common/packageUseProxyForm';
import { translateFn } from '../../../utils/translator.helper';
import { DownloadTypeFormWebComponent } from '../../../configuration/update/staging/downloadTypeForm';
import { PackagePanelType } from '../../packagesGrid/types';
import { LinkColor, LinkType } from '../../../ui/gzLinkComponent/types';

export class PackagePanelView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;

    $downloadTypeForm: DownloadTypeFormWebComponent;

    $generalDetailsSubtitle: GzTitleWebComponent;

    $packageGeneralFormWrapper: HTMLElement;
    $packageDeployerFormWrapper: HTMLElement;
    $packageGeneralForm: PackageGeneralFormWebComponent;

    $packageDeployerFormSubtitle: GzTitleWebComponent;
    $packageModulesForm: PackageModulesFormWebComponent;
    $packageDeployerForm: PackageDeployerFormWebComponent;
    $packageUseProxyForm: PackageUseProxyFormWebComponent;
    $packageSettingsForm: PackageSettingsFormWebComponent;
    $packageMiscellaneousForm: PackageMiscellaneousFormWebComponent;

    $packageScanModesForm: PackageScanModesFormWebComponent;

    $packageSecurityServerAssignmentGrid: PackageSecurityServerAssignmentGridWebComponent;
    $removeCompetitorInfo: GzInfoBoxWebComponent;

    $saveButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
  };

  private selectedPackage: PackageData | null;

  private panelTypeIsStaging: boolean;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),

      $downloadTypeForm: this.shadow.querySelector(WEB_COMPONENT_DOWNLOAD_TYPE_FORM),

      $packageGeneralFormWrapper: this.shadow.querySelector('#package-general-form-wrapper'),
      $packageDeployerFormWrapper: this.shadow.querySelector('#package-deployer-form-wrapper'),

      $generalDetailsSubtitle: this.shadow.querySelector('#general-details-subtitle'),
      $packageGeneralForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_GENERAL_FORM),

      $packageModulesForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_MODULES_FORM),
      $packageMiscellaneousForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_MISCELLANEOUS_FORM),

      $packageDeployerFormSubtitle: this.shadow.querySelector('#deployer-form-subtitle'),
      $packageDeployerForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_DEPLOYER_FORM),

      $packageUseProxyForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_USE_PROXY_FORM),

      $packageScanModesForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_SCAN_MODES_FORM),
      $packageSettingsForm: this.shadow.querySelector(WEB_COMPONENT_PACKAGE_SETTINGS_FORM),

      $packageSecurityServerAssignmentGrid: this.shadow.querySelector(
        WEB_COMPONENT_PACKAGE_SECURITY_SERVER_ASSIGNMENT_GRID
      ),

      $removeCompetitorInfo: this.shadow.querySelector('#remove-competitor-info'),
      $saveButton: this.shadow.querySelector('#save-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
    };

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$generalDetailsSubtitle.innerText = translateFn('General');
    this.dom.$packageDeployerFormSubtitle.innerText = translateFn('Deployer');

    let removeCompetitorInfoLink =
      'https://www.bitdefender.com/business/support/en/77212-376350-software-incompatible-with-best.html';
    if (this.dependencies.isCloud()) {
      removeCompetitorInfoLink =
        'https://www.bitdefender.com/business/support/en/77209-376350-software-incompatible-with-best.html';
      if (this.dependencies.isPartner()) {
        removeCompetitorInfoLink =
          'https://www.bitdefender.com/business/support/en/77211-376350-software-incompatible-with-best.html';
      }
    }

    this.dom.$removeCompetitorInfo.innerHTML = `${htmlEncode(
      translateFn('BEST will automatically uninstall other')
    )} <gz-link class='security-software-link-info' color="${LinkColor.SECONDARY}" type="${LinkType.UNDERLINE}"  href='${removeCompetitorInfoLink}'>
    <span>${htmlEncode(translateFn('security software.'))}</span>
    </gz-link>`;

    this.dom.$saveButton.innerText = this.commonStrings.save;
    this.dom.$cancelButton.innerText = translateFn('Cancel');
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(viewData: PackageViewData) {
    this.panelTypeIsStaging = viewData.panelType === PackagePanelType.STAGING;
    this.selectedPackage = viewData.package;

    this.dom.$downloadTypeForm.injectDependencies(this.dependencies);

    this.dom.$packageGeneralForm.formContext = viewData.context;
    this.dom.$packageGeneralForm.setPackageType(viewData.packageType);
    this.dom.$packageGeneralForm.setCompany(viewData.package?.company);
    this.dom.$packageGeneralForm.packageId = viewData.package?._id ?? null;
    this.dom.$packageGeneralForm.panelType = viewData.panelType;
    this.dom.$packageGeneralForm.injectDependencies(this.dependencies);

    this.dom.$packageModulesForm.formContext = viewData.context;
    this.dom.$packageModulesForm.selectedKitType = viewData.selectedKitType;
    this.dom.$packageModulesForm.setAvailableFeatures(viewData.availableFeatures);

    this.dom.$packageModulesForm.injectDependencies(this.dependencies);

    this.dom.$packageDeployerForm.formContext = viewData.context;
    this.dom.$packageDeployerForm.setCompanyId(viewData.package?.company.id);
    this.dom.$packageDeployerForm.injectDependencies(this.dependencies);

    this.dom.$packageUseProxyForm.formContext = viewData.context;
    this.dom.$packageUseProxyForm.setCompanyId(viewData.package?.company.id);
    this.dom.$packageUseProxyForm.injectDependencies(this.dependencies);

    this.dom.$packageScanModesForm.formContext = viewData.context;
    this.dom.$packageScanModesForm.setFeatures(viewData.availableFeatures);
    this.dom.$packageScanModesForm.injectDependencies(this.dependencies);

    this.dom.$packageMiscellaneousForm.injectDependencies(this.dependencies);

    this.dom.$packageSecurityServerAssignmentGrid.injectDependencies(this.dependencies);
    this.dom.$packageSecurityServerAssignmentGrid.companyId = viewData.package?.company.id ?? '';

    this.dom.$packageSettingsForm.injectDependencies(this.dependencies);
    this.dom.$packageSettingsForm.setPackageType(viewData.packageType);

    if (this.selectedPackage) {
      this.dom.$packageSettingsForm.setPackageData(this.selectedPackage);
      this.dom.$packageSecurityServerAssignmentGrid.setPackageData(this.selectedPackage).then().catch(console.error);
      this.dom.$packageDeployerForm.setPackageData(this.selectedPackage).then().catch(console.error);
      this.dom.$packageUseProxyForm.setPackageData(this.selectedPackage).then().catch(console.error);
    }

    toggleVisibility(this.dom.$packageSecurityServerAssignmentGrid, false);
    this.dom.$packageSecurityServerAssignmentGrid.setVisibility(false);

    const startWebCompPromises: Promise<void>[] = [
      this.dom.$packageGeneralForm.start(),
      this.dom.$packageModulesForm.start(),
      this.dom.$packageScanModesForm.start().then(() => {
        if (this.selectedPackage) {
          this.dom.$packageScanModesForm.setPackageData(this.selectedPackage, viewData.availableFeatures);
        }
      }),
      this.dom.$packageSecurityServerAssignmentGrid.start(),
      this.dom.$packageMiscellaneousForm.start().then(async () => {
        if (this.selectedPackage) {
          this.dom.$packageMiscellaneousForm.setPackageData(this.selectedPackage);
        }
      }),
      this.dom.$packageDeployerForm.start(),
      this.dom.$packageUseProxyForm.start(),
      this.dom.$packageSettingsForm.start(),
    ];

    toggleVisibility(this.dom.$downloadTypeForm, this.panelTypeIsStaging);

    if (this.panelTypeIsStaging) {
      startWebCompPromises.push(this.dom.$downloadTypeForm.start());
      this.dom.$saveButton.innerText = translateFn('Download');
    }

    Promise.all(startWebCompPromises)
      .then(() => {
        if (this.selectedPackage) {
          this.dom.$packageGeneralForm.setPackageData(this.selectedPackage).then().catch(console.error);
        }

        //for customers or onprem we should triggerCompanyChangeEvent programmatically because the company dropdown is not present
        if (!this.dom.$packageGeneralForm.isCompanyAvailable()) {
          this.dom.$packageGeneralForm.triggerCompanyChangeEvent(this.dependencies.getCurrentUserCompany().id);
        }

        this.removeLoadingState();

        this.dom.$wrapper.dispatchEvent(
          new CustomEvent(PackageEvent.OnViewInit, {
            bubbles: true,
            composed: true,
          })
        );
      })
      .catch(console.error);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public toggleVisibilityRemoveCompetitorInfo(isVisible: boolean): void {
    toggleVisibility(this.dom.$removeCompetitorInfo, isVisible);
  }

  public get hasUnsavedChanges(): boolean {
    return (
      this.dom.$packageGeneralForm.hasUnsavedChanges ||
      this.dom.$packageModulesForm.hasUnsavedChanges ||
      this.dom.$packageScanModesForm.hasUnsavedChanges ||
      this.dom.$packageDeployerForm.hasUnsavedChanges ||
      this.dom.$packageUseProxyForm.hasUnsavedChanges ||
      this.dom.$packageMiscellaneousForm.hasUnsavedChanges ||
      this.dom.$packageSecurityServerAssignmentGrid.hasUnsavedChanges ||
      this.dom.$packageSettingsForm.hasUnsavedChanges
    );
  }
}
